/**
 * Pulpo VTO (Virtual Try-On) Auto-Tracker
 *
 * Activated when:  data-custom-event-lib="pulpo"
 *
 * Intercepted events (fired automatically — no manual code needed):
 *  - productLog              Ürün görüntüleme / log
 *  - tryItOn                 VTO deneme başlatma
 *  - goToProduct             Ürün sayfasına git tıklaması
 *  - chooseModelButtonClick  Model seçim butonu tıklaması
 *  - modelSelectionClick     Model listesinden seçim
 *  - cameraButtonClick       Kamera aç butonu tıklaması
 *  - backButtonClick         Geri butonu tıklaması
 *  - tryItNowButtonClick     Şimdi dene butonu tıklaması
 *  - addToCart               Sepete ekleme
 *  - fallBackError           VTO yüklenemedi / fallback hatası
 *
 * Detection strategy (3 layers, priority order):
 *  1. window.pulpo / window.PulpoAR  global nesnesi
 *  2. DOM — [data-pulpo], .pulpo-widget, #pulpo-container, iframe[src*="pulpo"]
 *  3. Script tag — src içinde "pulpo" geçiyor mu
 */

export default class PulpoTracker {
  constructor(b2mTracker) {
    this.b2mTracker = b2mTracker;
    this.isInitialized = false;

    // ── Selector sets ──────────────────────────────────────────────────────
    this.selectors = {
      // Widget root containers
      root: [
        '[data-pulpo]',
        '.pulpo-widget',
        '.pulpo-container',
        '#pulpo-container',
        '#pulpo-widget',
        '#pulpo-app',
        'iframe[src*="pulpo"]'
      ].join(', '),

      // "Şimdi Dene / Try It Now" button
      tryItNow: [
        '[data-pulpo-action="try-it-now"]',
        '[data-action="try-it-now"]',
        '.pulpo-try-btn',
        '.try-it-now',
        '.try-now-btn',
        '#btn-try-it-now'
      ].join(', '),

      // Camera / AR başlat button
      camera: [
        '[data-pulpo-action="camera"]',
        '[data-action="camera"]',
        '.pulpo-camera-btn',
        '.camera-btn',
        '#btn-camera'
      ].join(', '),

      // Model seç (choose model) button
      chooseModel: [
        '[data-pulpo-action="choose-model"]',
        '[data-action="choose-model"]',
        '.pulpo-choose-model',
        '.choose-model-btn',
        '#btn-choose-model'
      ].join(', '),

      // Model list items
      modelItem: [
        '[data-pulpo-model]',
        '.pulpo-model-item',
        '.model-item',
        '.model-option'
      ].join(', '),

      // "Ürüne Git" button
      goToProduct: [
        '[data-pulpo-action="go-to-product"]',
        '[data-action="go-to-product"]',
        '.pulpo-go-to-product',
        '.go-to-product-btn',
        '#btn-go-to-product'
      ].join(', '),

      // "Sepete Ekle" button
      addToCart: [
        '[data-pulpo-action="add-to-cart"]',
        '[data-action="add-to-cart"]',
        '.pulpo-add-to-cart',
        '.add-to-cart-btn',
        '#btn-add-to-cart'
      ].join(', '),

      // "Geri" button
      back: [
        '[data-pulpo-action="back"]',
        '[data-action="back"]',
        '.pulpo-back-btn',
        '.back-btn',
        '#btn-back'
      ].join(', ')
    };
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  PUBLIC
  // ══════════════════════════════════════════════════════════════════════════

  init() {
    if (this.isInitialized) return;

    console.log('[B2M Pulpo] 🚀 Initializing Pulpo VTO auto-tracker...');

    // Layer 1: Intercept native Pulpo SDK callbacks (highest priority)
    this._interceptPulpoSDK();

    // Layer 2: DOM event delegation — works with any custom widget HTML
    this._setupDOMListeners();

    // Layer 3: MutationObserver — widget may be injected after SDK init
    this._setupMutationObserver();

    // Layer 4: postMessage listener — for cross-origin iframe widgets
    this._setupPostMessageListener();

    // Track initial product log when widget is present on page
    this._trackInitialProductLog();

    this.isInitialized = true;
    console.log('[B2M Pulpo] ✅ Pulpo VTO auto-tracker ready');
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  LAYER 1 — Native Pulpo SDK callback interception
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * Intercept window.pulpo / window.PulpoAR callback properties.
   * Pulpo SDK fires these as: window.pulpo.onAddToCart({ productId, variantId })
   * We wrap them so they still work AND we send to B2M simultaneously.
   */
  _interceptPulpoSDK() {
    // Callback map: pulpoCallbackName → b2m event name + property extractor
    const CALLBACK_MAP = {
      onProductLog:             { event: 'productLog',             extract: d => ({ product_id: d?.productId, product_name: d?.productName, category: d?.category, ...d }) },
      onTryItOn:                { event: 'tryItOn',                extract: d => ({ product_id: d?.productId, variant_id: d?.variantId, ...d }) },
      onGoToProduct:            { event: 'goToProduct',            extract: d => ({ product_id: d?.productId, product_url: d?.productUrl || d?.url, ...d }) },
      onChooseModelButtonClick: { event: 'chooseModelButtonClick', extract: d => ({ product_id: d?.productId, ...d }) },
      onModelSelectionClick:    { event: 'modelSelectionClick',    extract: d => ({ product_id: d?.productId, model_id: d?.modelId, model_name: d?.modelName, ...d }) },
      onCameraButtonClick:      { event: 'cameraButtonClick',      extract: d => ({ product_id: d?.productId, variant_id: d?.variantId, ...d }) },
      onBackButtonClick:        { event: 'backButtonClick',        extract: d => ({ product_id: d?.productId, from_screen: d?.fromScreen || d?.screen, ...d }) },
      onTryItNowButtonClick:    { event: 'tryItNowButtonClick',    extract: d => ({ product_id: d?.productId, variant_id: d?.variantId, ...d }) },
      onAddToCart:              { event: 'addToCart',              extract: d => ({ product_id: d?.productId, variant_id: d?.variantId, product_name: d?.productName, price: d?.price, currency: d?.currency || 'TRY', quantity: d?.quantity || 1, ...d }) },
      onFallBackError:          { event: 'fallBackError',          extract: d => ({ product_id: d?.productId, error_message: d?.message || d?.errorMessage, error_code: d?.code || d?.errorCode, ...d }) }
    };

    // Try both window.pulpo and window.PulpoAR namespaces
    const tryWrap = (namespace) => {
      const obj = window[namespace];
      if (!obj || typeof obj !== 'object') return;

      for (const [cbName, mapping] of Object.entries(CALLBACK_MAP)) {
        const original = obj[cbName];

        // Define a setter so future assignments are also wrapped
        Object.defineProperty(obj, cbName, {
          configurable: true,
          get() { return this[`_b2m_${cbName}`]; },
          set: (fn) => {
            obj[`_b2m_${cbName}`] = (...args) => {
              // Call the original Pulpo callback
              if (typeof fn === 'function') fn(...args);

              // Send to B2M
              try {
                const data = args[0] || {};
                this.b2mTracker.track(mapping.event, mapping.extract(data));
              } catch (e) {
                console.warn(`[B2M Pulpo] Error tracking ${mapping.event}:`, e);
              }
            };
          }
        });

        // Wrap any existing assignment
        if (typeof original === 'function') {
          obj[cbName] = original;
        }
      }

      console.log(`[B2M Pulpo] SDK callbacks wrapped on window.${namespace}`);
    };

    tryWrap('pulpo');
    tryWrap('PulpoAR');

    // Re-try after a short delay in case Pulpo SDK loads after B2M
    setTimeout(() => {
      tryWrap('pulpo');
      tryWrap('PulpoAR');
    }, 1500);
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  LAYER 2 — DOM event delegation
  // ══════════════════════════════════════════════════════════════════════════

  _setupDOMListeners() {
    // Single delegated click listener on document
    document.addEventListener('click', (e) => {
      this._handleClick(e);
    }, true); // capture phase — fires before widget's own handlers

    console.log('[B2M Pulpo] DOM click delegation active');
  }

  _handleClick(e) {
    const target = e.target;

    // ── tryItNow ────────────────────────────────────────────────────────────
    if (target.closest(this.selectors.tryItNow)) {
      const el = target.closest(this.selectors.tryItNow);
      this.b2mTracker.track('tryItNowButtonClick', {
        product_id: this._attr(el, 'data-product-id'),
        variant_id: this._attr(el, 'data-variant-id')
      });
      return;
    }

    // ── camera ──────────────────────────────────────────────────────────────
    if (target.closest(this.selectors.camera)) {
      const el = target.closest(this.selectors.camera);
      this.b2mTracker.track('cameraButtonClick', {
        product_id: this._attr(el, 'data-product-id'),
        variant_id: this._attr(el, 'data-variant-id')
      });
      return;
    }

    // ── chooseModel ─────────────────────────────────────────────────────────
    if (target.closest(this.selectors.chooseModel)) {
      const el = target.closest(this.selectors.chooseModel);
      this.b2mTracker.track('chooseModelButtonClick', {
        product_id: this._attr(el, 'data-product-id')
      });
      return;
    }

    // ── modelItem ───────────────────────────────────────────────────────────
    if (target.closest(this.selectors.modelItem)) {
      const el = target.closest(this.selectors.modelItem);
      this.b2mTracker.track('modelSelectionClick', {
        product_id: this._attr(el, 'data-product-id'),
        model_id:   this._attr(el, 'data-model-id') || this._attr(el, 'data-pulpo-model'),
        model_name: this._attr(el, 'data-model-name') || el.textContent?.trim().substring(0, 80)
      });
      return;
    }

    // ── goToProduct ─────────────────────────────────────────────────────────
    if (target.closest(this.selectors.goToProduct)) {
      const el = target.closest(this.selectors.goToProduct);
      this.b2mTracker.track('goToProduct', {
        product_id:  this._attr(el, 'data-product-id'),
        product_url: this._attr(el, 'data-url') || el.href || ''
      });
      return;
    }

    // ── addToCart ───────────────────────────────────────────────────────────
    if (target.closest(this.selectors.addToCart)) {
      const el = target.closest(this.selectors.addToCart);
      this.b2mTracker.track('addToCart', {
        product_id:   this._attr(el, 'data-product-id'),
        variant_id:   this._attr(el, 'data-variant-id'),
        product_name: this._attr(el, 'data-product-name'),
        price:        parseFloat(this._attr(el, 'data-price')) || undefined,
        currency:     this._attr(el, 'data-currency') || 'TRY',
        quantity:     parseInt(this._attr(el, 'data-quantity')) || 1
      });
      return;
    }

    // ── back ────────────────────────────────────────────────────────────────
    if (target.closest(this.selectors.back)) {
      const el = target.closest(this.selectors.back);
      this.b2mTracker.track('backButtonClick', {
        product_id:  this._attr(el, 'data-product-id'),
        from_screen: this._attr(el, 'data-from-screen') || 'vto_preview'
      });
      return;
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  LAYER 3 — MutationObserver (widget injected after SDK)
  // ══════════════════════════════════════════════════════════════════════════

  _setupMutationObserver() {
    const observer = new MutationObserver(() => {
      this._trackInitialProductLog();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Stop observing after 10 seconds to avoid overhead
    setTimeout(() => observer.disconnect(), 10000);
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  LAYER 4 — postMessage (cross-origin iframe widget)
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * Pulpo widget (or any VTO widget) can send postMessage events like:
   *   window.parent.postMessage({ type: 'pulpo:addToCart', data: { ... } }, '*')
   */
  _setupPostMessageListener() {
    const EVENT_MAP = {
      'pulpo:productLog':             'productLog',
      'pulpo:tryItOn':                'tryItOn',
      'pulpo:goToProduct':            'goToProduct',
      'pulpo:chooseModelButtonClick': 'chooseModelButtonClick',
      'pulpo:modelSelectionClick':    'modelSelectionClick',
      'pulpo:cameraButtonClick':      'cameraButtonClick',
      'pulpo:backButtonClick':        'backButtonClick',
      'pulpo:tryItNowButtonClick':    'tryItNowButtonClick',
      'pulpo:addToCart':              'addToCart',
      'pulpo:fallBackError':          'fallBackError'
    };

    window.addEventListener('message', (e) => {
      if (!e.data || typeof e.data !== 'object') return;

      const msgType = e.data.type;
      if (!msgType) return;

      const b2mEvent = EVENT_MAP[msgType];
      if (b2mEvent) {
        const data = e.data.data || e.data.payload || {};
        this.b2mTracker.track(b2mEvent, {
          ...data,
          _source: 'postMessage'
        });
        console.log(`[B2M Pulpo] postMessage captured: ${msgType} → ${b2mEvent}`);
      }
    });

    console.log('[B2M Pulpo] postMessage listener active');
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  INITIAL PRODUCT LOG
  // ══════════════════════════════════════════════════════════════════════════

  _trackInitialProductLog() {
    // Don't fire twice
    if (this._productLogSent) return;

    const root = document.querySelector(this.selectors.root);
    if (!root) return;

    // Already tracked this root element
    if (root.dataset.b2mPulpoTracked) return;
    root.dataset.b2mPulpoTracked = 'true';
    this._productLogSent = true;

    const productId   = this._attr(root, 'data-product-id')   || this._attr(root, 'data-pulpo-product');
    const productName = this._attr(root, 'data-product-name') || this._attr(root, 'data-pulpo-product-name');
    const category    = this._attr(root, 'data-category')     || this._attr(root, 'data-pulpo-category');

    this.b2mTracker.track('productLog', {
      product_id:   productId,
      product_name: productName,
      category:     category,
      _source:      'auto_dom'
    });

    // Also fire tryItOn — widget opening = user intent to try
    this.b2mTracker.track('tryItOn', {
      product_id:  productId,
      variant_id:  this._attr(root, 'data-variant-id') || this._attr(root, 'data-pulpo-variant'),
      _source:     'auto_dom'
    });

    console.log('[B2M Pulpo] Initial productLog + tryItOn sent');
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  HELPERS
  // ══════════════════════════════════════════════════════════════════════════

  /** Safely read a data attribute, return null if missing */
  _attr(el, name) {
    if (!el) return null;
    const val = el.getAttribute(name) || el.dataset[this._camel(name.replace(/^data-/, ''))];
    return val || null;
  }

  /** Convert kebab-case to camelCase for dataset access */
  _camel(str) {
    return str.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
  }
}
